import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { EM_DASH, formatNumber } from './queueMonitorFormat';
import {
	emailTrackingUrl,
	OLDEST_FIRST,
	STALE_ROW_TYPES,
} from 'pages/_rd_dashboard/AdminEmailTracking/emailTrackingLinks';

/**
 * EmailOutboxPanel
 *
 * The Queue Monitor's Email outbox view, fed by GET /api/violerts-queue/outbox:
 * the same snapshot the backend's alert_outbox_stalled and outbox_rows_stale
 * health rules read, so the numbers here are the numbers the pager used.
 *
 * - outbox null (backend without the endpoint, deploy skew) -> renders nothing.
 * - per-type table: pending rows, oldest pending age, live rows over 3 h and
 *   over 26 h; the cells a firing rule reads are red.
 * - hourly sends strip: hourly alert sends of the ET day, 06:00-20:00, zeros
 *   included, so a stall shows as a run of empty hours.
 * - waiting users: the users an hourly alert is overdue for, each linking to
 *   their pending alert rows in Email Tracking.
 */

export const OUTBOX_CODES = {
	alertsStalled: 'alert_outbox_stalled',
	staleRows: 'outbox_rows_stale',
};

const TYPE_LABELS = {
	alert: 'Alert',
	notification: 'Notification',
	expiring1: 'Expiring in 1 day',
	expiring7: 'Expiring in 7 days',
	expiring14: 'Expiring in 14 days',
	reminder: 'Reminder',
};

const MINUTES_PER_HOUR = 60;

// Whole minutes -> compact age ("45m", "3h 30m", "2h"); null -> em dash.
export const formatMinutes = (minutes) => {
	if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) {
		return EM_DASH;
	}
	const m = Math.max(0, Math.floor(minutes));
	if (m < MINUTES_PER_HOUR) {
		return `${m}m`;
	}
	const h = Math.floor(m / MINUTES_PER_HOUR);
	const rest = m % MINUTES_PER_HOUR;
	return rest > 0 ? `${h}h ${rest}m` : `${h}h`;
};

const formatHour = (hour) => `${String(hour).padStart(2, '0')}:00`;

const userAlertsUrl = (email) =>
	emailTrackingUrl({ search: email, type: 'alert', status: 'pending', ...OLDEST_FIRST });

const EmailOutboxPanel = ({ outbox }) => {
	if (!outbox || typeof outbox !== 'object') {
		return null;
	}

	const firing = outbox.firing || {};
	const stall = outbox.alert_stall || {};
	const alertsStalled = firing[OUTBOX_CODES.alertsStalled] === true;
	const rowsStale = firing[OUTBOX_CODES.staleRows] === true;
	const types = Array.isArray(outbox.types) ? outbox.types : [];
	const hours = Array.isArray(outbox.hourly_sends_today) ? outbox.hourly_sends_today : [];
	const users = Array.isArray(stall.users) ? stall.users : [];
	const waiting = stall.waiting_users || 0;

	return (
		<div className="analytics-card email-outbox-panel mb-4">
			<div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
				<h5 className="mb-0 me-2">Email outbox</h5>
				<span className="small">
					{alertsStalled && <span className="badge bg-warning text-dark me-1">{OUTBOX_CODES.alertsStalled}</span>}
					{rowsStale && <span className="badge bg-warning text-dark">{OUTBOX_CODES.staleRows}</span>}
				</span>
			</div>

			{outbox.in_send_window === false && (
				<p className="text-muted small mb-2">
					<i className="bi bi-moon me-2" aria-hidden="true" />
					Hourly alerts are outside the 06:00-21:00 ET send window, so no user counts as waiting.
				</p>
			)}

			<p className={`mb-3 ${alertsStalled ? 'text-danger fw-semibold' : 'text-muted'}`}>
				{waiting > 0
					? `${formatNumber(waiting)} ${waiting === 1 ? 'user' : 'users'} under the daily cap waited over ${stall.wait_threshold_min} min (longest ${formatMinutes(stall.longest_wait_min)})`
					: 'No user is waiting on an overdue hourly alert'}
			</p>

			<div className="table-responsive mb-3">
				<table className="table table-sm mb-0">
					<thead>
						<tr>
							<th>Type</th>
							<th className="text-end">Pending</th>
							<th className="text-end">Oldest pending</th>
							<th className="text-end">Over 3 h</th>
							<th className="text-end">Over 26 h</th>
						</tr>
					</thead>
					<tbody>
						{types.map((row) => {
							const staleCell = rowsStale && STALE_ROW_TYPES.includes(row.type) && row.stale_rows > 0;
							const pendingCell = alertsStalled && row.type === 'alert';
							return (
								<tr key={row.type} data-type={row.type}>
									<td>{TYPE_LABELS[row.type] || row.type}</td>
									<td data-col="pending" className={`text-end ${pendingCell ? 'table-danger' : ''}`}>
										{formatNumber(row.pending)}
									</td>
									<td className="text-end">{formatMinutes(row.oldest_pending_age_min)}</td>
									<td className="text-end">{formatNumber(row.aged_rows)}</td>
									<td data-col="stale" className={`text-end ${staleCell ? 'table-danger' : ''}`}>
										{formatNumber(row.stale_rows)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			<h6 className="mb-1">
				Hourly alerts sent today <span className="text-muted small">(ET)</span>
			</h6>
			<div className="d-flex flex-wrap gap-1 mb-3 outbox-hours">
				{hours.map(({ hour, sent }) => (
					<div
						key={hour}
						data-hour={hour}
						className={`outbox-hour text-center border rounded px-2 py-1 ${sent > 0 ? '' : 'outbox-hour-empty text-muted'}`}
						title={`${formatHour(hour)} ET`}
					>
						<div className="small text-muted">{formatHour(hour)}</div>
						<div className="fw-semibold">{formatNumber(sent)}</div>
					</div>
				))}
			</div>

			{users.length > 0 && (
				<div className="table-responsive">
					<table className="table table-sm mb-0">
						<thead>
							<tr>
								<th>Waiting user</th>
								<th className="text-end">Oldest row</th>
								<th className="text-end">Wait</th>
							</tr>
						</thead>
						<tbody>
							{users.map((user) => (
								<tr key={user.user_id}>
									<td>
										<Link to={userAlertsUrl(user.email)}>{user.email}</Link>
									</td>
									<td className="text-end">{user.oldest_row_id}</td>
									<td className="text-end">{formatMinutes(user.wait_minutes)}</td>
								</tr>
							))}
						</tbody>
					</table>
					{waiting > users.length && (
						<p className="text-muted small mt-1 mb-0">
							Showing the {users.length} longest waits of {formatNumber(waiting)}.
						</p>
					)}
				</div>
			)}
		</div>
	);
};

EmailOutboxPanel.propTypes = {
	outbox: PropTypes.object,
};

EmailOutboxPanel.defaultProps = {
	outbox: null,
};

export default EmailOutboxPanel;
